import { Metric, Value } from "@/entities/Finance/Finance.model";
import { filterMetricValuesByWindow } from "./filterPeriodsByWindow";
import { AccountLabel } from "@/entities/Labeling/Account.model";
import { FinanceLabField } from "@/entities/FinanceLab/FinanceLab.model";
import { BALANCE_SHEET_LABELS } from "../metrics.const";

function calculateLastTwoPeriodsDeltas(values: Value[]): number | null {
    if (values.length < 2) return null;
    const sorted = [...values].sort((a, b) => a.period.localeCompare(b.period));
    const lastValue = sorted[sorted.length - 1].value;
    const secondLastValue = sorted[sorted.length - 2].value;
    if (secondLastValue === 0) return null;
    const delta = (lastValue - secondLastValue) / secondLastValue;
    return delta === 0 ? 0 : delta;
}

function calculateCumulativeDelta(values: Value[]): number | null {
    if (values.length < 2) return null;
    const sorted = [...values].sort((a, b) => a.period.localeCompare(b.period));

    let totalDelta = 0;
    let comparisons = 0;
    for (let i = 1; i < sorted.length; i++) {
        const previousValue = sorted[i - 1].value;
        if (previousValue === 0) continue;
        const currentValue = sorted[i].value;
        const periodDelta = (currentValue - previousValue) / previousValue;
        totalDelta += periodDelta;
        comparisons++;
    }

    return comparisons > 0 ? totalDelta : null;
}

function isBalanceSheetMetric(label: string): boolean {
    return BALANCE_SHEET_LABELS.has(label as AccountLabel | FinanceLabField);
}

/**
 * Apply window filtering to a metric and recalculate delta based on filtered values
 * @param metric - The metric to filter
 * @param windowId - Window ID from period selector (e.g., "l12m", "ytd", "all")
 * @returns Metric with filtered values and recalculated delta
 */
export function applyWindowToMetric(
    metric: Metric,
    windowId: string | undefined,
): Metric {
    // Filter values based on window
    const filteredValues = filterMetricValuesByWindow(metric.values, windowId);

    // Recalculate delta based on filtered values
    const isBalanceSheet = isBalanceSheetMetric(metric.info.derived.label);
    const delta = isBalanceSheet
        ? calculateLastTwoPeriodsDeltas(filteredValues)
        : calculateCumulativeDelta(filteredValues);

    return {
        ...metric,
        values: filteredValues,
        delta,
    };
}

/**
 * Apply window filtering to all metrics in a record
 * @param metrics - Record of metrics
 * @param windowId - Window ID from period selector
 * @returns Record of metrics with filtered values and recalculated deltas
 */
export function applyWindowToMetrics(
    metrics: Record<string, Metric>,
    windowId: string | undefined,
): Record<string, Metric> {
    if (!windowId || windowId === "all") {
        return metrics;
    }

    return Object.fromEntries(
        Object.entries(metrics).map(([key, metric]) => [
            key,
            applyWindowToMetric(metric, windowId),
        ]),
    );
}
